/* eslint-disable no-console */
import { Temporal } from "@hermes/intl";
import { useSuspense } from "@hermes/ui";
import { type ProfilerOnRenderCallback } from "react";

export const capitalize = (str: string) => str[0]?.toUpperCase() + str.slice(1);

export type GeoPosition = {
  lat?: number;
  lng?: number;
};

type ProfilerRecord = {
  id: string;
  phase: "mount" | "update" | "nested-update" | "other";
  actualDuration: number;
};

export const profilerStats = {
  records: [] as ProfilerRecord[],
  log: (record: ProfilerRecord) => {
    profilerStats.records.push(record);
  },
  runWithLog: <F extends () => any>(
    callback: F,
    log: Pick<ProfilerRecord, "id" | "phase">,
  ): ReturnType<F> => {
    const start = Temporal.Now.plainTimeISO();
    const result = callback();
    const end = Temporal.Now.plainTimeISO();

    profilerStats.log({
      id: log.id,
      phase: log.phase,
      actualDuration: end.since(start).total("milliseconds"),
    });

    return result;
  },
  summary: () => {
    const round = (num: number) => Math.round(num * 100000) / 100000;
    console.group("[profilerStats] summary");

    const groups = profilerStats.records.groupBy((record) => record.id);

    for (const id in groups) {
      const group = groups[id];
      if (!group) continue;

      console.group(`${id} - total ${group.length} records`);

      const phases = group.groupBy((record) => record.phase);

      for (const phase in phases) {
        const records = phases[phase as keyof typeof phases];

        console.group(`${phase} - total ${records.length} records`);

        let sum = 0;
        let min = Number.MAX_VALUE;
        let max = Number.MIN_VALUE;

        for (const record of records) {
          sum += record.actualDuration;
          min = Math.min(min, record.actualDuration);
          max = Math.max(max, record.actualDuration);
        }

        console.info(`Total time: ${round(sum)}ms`);
        console.info(`Min: ${round(min)}ms`);
        console.info(`Max: ${round(max)}ms`);
        console.info(`Average: ${round(sum / records.length)}ms`);
        const rounded = records
          .map((record) => round(record.actualDuration))
          .sort((a, b) => a - b);

        console.info(`Median: ${rounded[Math.floor(rounded.length / 2)]}ms`);
        const roundedGroups = Object.entries(rounded.groupBy((round) => round))
          .map(([duration, durations]) => [duration, durations.length] as const)
          .sort((a, b) => b[1] - a[1]);

        const mode = roundedGroups[0];
        if (mode && mode[1] !== 1) {
          console.info(
            `Mode: ${mode[0]}ms with ${mode[1]} repetitions throughout ${roundedGroups.length} groups`,
          );
        }

        console.groupEnd();
      }

      console.groupEnd();
    }

    console.groupEnd();
  },
};

(window as any).profilerStats = profilerStats;

export function onProfilerRenderCallback(
  filters: {
    minDuration?: number;
    phase?: "update" | "mount" | "nested-update";
  } = {},
): ProfilerOnRenderCallback {
  return (id, phase, actualDuration) => {
    if (filters.phase && filters.phase === phase) return;
    if (filters.minDuration && actualDuration < filters.minDuration) return;
    if (actualDuration !== 0) {
      profilerStats.log({ id, phase, actualDuration });
    }
  };
}

const getCurrentPositionAsync = () => {
  return new Promise<
    | { status: true; data: GeoPosition }
    | { status: false; error: GeolocationPositionError }
  >((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        resolve({
          status: true,
          data: {
            lat: coords.latitude,
            lng: coords.longitude,
          },
        });
      },
      (err) => {
        resolve({
          status: false,
          error: err,
        });
      },
    );
  });
};

export const useGeolocation = ({ enabled }: { enabled?: boolean }) => {
  const data = useSuspense(
    "position",
    () => getCurrentPositionAsync(),
    enabled,
  );

  return {
    position: data?.status === true ? data.data : undefined,
    error: data?.status === false ? data.error : undefined,
  };
};

type Primitive = string | number | symbol | undefined;

type MatchPattern<T extends Primitive> = {
  [K in Exclude<T, undefined>]?: () => any;
} & {
  undefined?: () => any;
  _?: () => any;
};

type ExtractReturnType<P> = P[keyof P] extends (...args: any) => any
  ? ReturnType<P[keyof P]>
  : never;

export function match<T extends Primitive, P extends MatchPattern<T>>(
  value: T,
  pattern: P,
): ExtractReturnType<P> {
  if (value === undefined && pattern.undefined) {
    return pattern.undefined();
  }

  if (value && value in pattern) {
    const fn = pattern[value as keyof P];
    if (typeof fn === "function") {
      return fn();
    }
  }

  if ("_" in pattern && pattern._) {
    return pattern._();
  }

  throw new Error(
    `Non-exhaustive match: no handler for value "${String(value)}"`,
  );
}

export async function hashSHA256(input: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
}
